import { Box, Typography } from '@mui/material';
import DrawToolbar from '../esri/DrawToolbar.tsx';
import { useEffect, useRef, useState } from 'react';
import UserBookmarksService from '../../services/UserBookmarksService';
import FieldNames from '../../defaults/FieldNames.json';
import Graphic from '@arcgis/core/Graphic';
import Bookmark from '@arcgis/core/webmap/Bookmark';
import ErrorService from '../../services/ErrorService';
import Point from '@arcgis/core/geometry/Point';
import {
    actualActivityActions,
    actualTreatmentActions,
    additionalPolygonActions,
    buildCountQuery,
    buildQuery,
    configureActions,
    estimatedActivityActions,
    estimatedTreatmentActions,
    flattenToAttributes,
    getLayerFromMapByPortalIdAndSetInitState,
    getLayerFromMapByUrl,
    initializeWebMapView,
    projectActions,
    queryAllFeatures,
    queryFeatureCount,
} from '../../utils/MapUtilities.tsx';
import './Map.css';
import {useDispatch, useSelector} from 'react-redux';
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils.js';
import type FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import LayerList from '@arcgis/core/widgets/LayerList';
import Expand from '@arcgis/core/widgets/Expand';
import type WebMap from '@arcgis/core/WebMap';
import type { FL } from '../../types/esri-extensions.tsx';
import { setTreatments } from '../../redux/slices/treatmentsSlice';
import { setActivities } from '../../redux/slices/activitiesSlice';
import { setActualsTreatments } from '../../redux/slices/actualsTreatmentsSlice';
import { setActualsActivities } from '../../redux/slices/actualsActivitiesSlice';
import { LayerGroupService } from '../../services/LayerGroupService.ts';
import FeatureSet from '@arcgis/core/rest/support/FeatureSet';
import { updateFilterAndDefinitionExpression } from '../../utils/ExpressionHelper';
import type MapView from '@arcgis/core/views/MapView';
import {
    CopyListeners,
    CreateListeners,
    GetListeners,
    highlightFeatureFromURL,
    IconListeners,
    MapListeners,
    ModifiedListeners,
    RefreshListeners,
    ResetListeners,
    SetListeners,
    WFIGSValueSelectionListener,
} from '../esri/MapEventHelper.tsx';
import { setVisibleProjects } from '../../redux/slices/detailsSlice';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import {
    getLegacyModifiedAttributes,
    getWFFTSModifiedAttributes,
    isFeaturePostFire,
} from '../../utils/FeatureHelper';
import { useLocation, useNavigate } from 'react-router-dom';
import Polygon from '@arcgis/core/geometry/Polygon';
import SpatialReference from '@arcgis/core/geometry/SpatialReference';
import * as WebMercatorUtils from '@arcgis/core/geometry/support/webMercatorUtils';
import * as geometryJsonUtils from '@arcgis/core/geometry/support/jsonUtils';
import * as GeometryEngine from '@arcgis/core/geometry/geometryEngine';
import {
    getMeasureButtons,
    GetSearchWidget,
    MapViewHelper,
    NewBookmarkWidget,
    SetBookmarkWidget,
} from '../esri/MapHelper.tsx';
import BasemapToggle from '@arcgis/core/widgets/BasemapToggle';
import { setCurrentMapViewObj } from '../../redux/slices/mapSlice';
import { emitCustomEvent } from 'react-custom-events';
import Events from '../../defaults/Events.jsx';
import GeneralService from '../../services/GeneralService';
import {
    setFiscalYearOverridesActive,
    setFiscalYearOverridesDate,
    setObjectId,
} from '../../redux/slices/globalOverridesSlice';
import { configureFiscalYearOverrides } from '../../utils/OverrideHelper';
import Query from '@arcgis/core/rest/support/Query';

import {
    setShowListCardLoader,
    setTreatmentCountQueryError,
    setTreatmentCount,
    setActivityCount,
    setActualTreatmentCount,
    setActualActivityCount,
    setActivityCountQueryError,
    setActualTreatmentCountQueryError,
    setActualActivityCountQueryError,
    setIsTreatmentCountQuerying,
    setIsActivityCountQuerying,
    setIsActualTreatmentCountQuerying,
    setIsActualActivityCountQuerying,
    setAlertMessageObj,
} from '../../redux/slices/globalContextSlice.jsx';
import type {RootState} from "../../redux/store.tsx";

export default function Map({
    isConfigLoaded,
    setMapSnackbarMessage,
    setMapSnackbarOpen,
    setMapSnackbarSeverity,
    listOpen,
}: {
    isConfigLoaded: boolean;
    setMapSnackbarMessage: (msg: string) => void;
    setMapSnackbarOpen: (open: boolean) => void;
    setMapSnackbarSeverity: (severity: string) => void;
    listOpen: boolean;
}) {
    const userInfo = useSelector((state: RootState) => state.globalContext.userInfo);
    const userRoleInfo = useSelector((state: RootState) => state.globalContext.userRoleInfo);

    const polygonListCount = useSelector((state: RootState) => state.globalContext.polygonListCount);
    const lineListCount = useSelector((state: RootState) => state.globalContext.lineListCount);
    const pointListCount = useSelector((state: RootState) => state.globalContext.pointListCount);
    const sortByValue = useSelector((state: RootState) => state.globalContext.sortByValue);
    const projectSortByValue = useSelector((state: RootState) => state.globalContext.projectSortByValue);
    const alertMessageObj = useSelector((state: RootState) => state.globalContext.alertMessageObj);
    const showMap = useSelector((state: RootState) => state.displayState.showMap);

    let viewExtentListener: __esri.Handle | null = null;
    const mapRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();

    // state stuff
    const [view, setView] = useState<__esri.MapView | null>(null);
    const [isBookmarksLoaded, setIsBookmarksLoaded] = useState(false);
    const [featureAbortController, setFeatureAbortController] = useState<AbortController | null>(
        null
    );
    const [featureCountAbortController, setFeatureCountAbortController] =
        useState<AbortController | null>(null);
    const [projectAbortController, setProjectAbortController] = useState<AbortController | null>(
        null
    );
    const [existingBookmarkData, setExistingBookmarkData] = useState<Bookmark[]>([]);
    const [extent, setExtent] = useState<__esri.Extent | null>(null);
    const [mapGeoExtentToLoad, setMapGeoExtentToLoad] = useState(null);
    const [spatialFilterGraphicsLayer, setSpatialFilterGraphicsLayer] =
        useState<GraphicsLayer | null>(null);
    const [modifiedGraphicsLayer, setModifiedGraphicsLayer] = useState<GraphicsLayer | null>(null);
    // const [tempGraphicsLayer, setTempGraphicsLayer] = useState<GraphicsLayer | null>(null);
    const [legacyAttributes, setLegacyAttributes] = useState(null);
    const [existingGeoBeforeEdit, setExistingGeoBeforeEdit] = useState(null);
    const [layerListWidget, setLayerListWidget] = useState<LayerList | null>(null);
    const [wfftsAttributes, setWfftsAttributes] = useState(null);
    const [actualIsNew, setActualIsNew] = useState(false);
    const [plannedFeatureAttributes, setPlannedFeatureAttributes] = useState(null);
    const [actualIsTreatment, setActualIsTreatment] = useState(true);
    const [mapSearchWidget, setMapSearchWidget] = useState(null);
    const [mapMeasureWidget, setMapMeasureWidget] = useState(null);
    const [initLoaded, setInitLoaded] = useState(false);

    // redux stuff
    const dispatch = useDispatch();
    const currentMapExtent = useSelector((state: any) => state.map?.currentMapExtent);
    const filter = useSelector((state: any) => state?.filter);
    const isViewingActions = useSelector((state: any) => state.menu?.viewingActions);
    const isViewingProjects = useSelector((state: any) => state.menu?.viewingProjects);
    const config = useSelector((state: any) => state.configuration.config);
    const definitionExpression = useSelector((state: any) => state.filter.definitionExpression)
        ?.replaceAll('&lt;', '<')
        .replaceAll('&gt;', '>');
    const filterFeatureSelection = useSelector((state: any) => state.filter.filterFeatureSelection);
    const dataset = useSelector((state: any) => state.filter.dataset);
    const filterGeometry = useSelector((state: any) => state.filter.filterGeometry);
    const isFilterOnly = useSelector((state: any) => state.filter.isLocationFilterOnly);
    const overrides = useSelector((state) => state.globalOverrides);

    // filter states
    const isBasicFilterActive = filter.isBasicFilterActive;
    const isCustomFilterActive = filter.isCustomFilterActive;

    // layer configs
    const postFireEstimatedTreatments = useRef<LayerGroupService | null>(null);
    const postFireActualsTreatments = useRef<LayerGroupService | null>(null);

    const postFireEstimatedActivities = useRef<LayerGroupService | null>(null);
    const postFireActualsActivities = useRef<LayerGroupService | null>(null);

    const fuelsEstimatedTreatments = useRef<FeatureLayer | null>(null);
    const fuelsActualsTreatments = useRef<FeatureLayer | null>(null);

    const fuelsEstimatedActivities = useRef<FeatureLayer | null>(null);
    const fuelsActualActivities = useRef<FeatureLayer | null>(null);

    const postFireProjectsPolygonLayer = useRef<FeatureLayer | null>(null);
    const postFireValuesPolygonLayer = useRef<FeatureLayer | null>(null);
    const legacyTreatmentsLayer = useRef<FeatureLayer | null>(null);
    const wfftsLayer = useRef<FeatureLayer | null>(null);
    const ifphLayer = useRef<FeatureLayer | null>(null);
    const wfigsLayer = useRef<FeatureLayer | null>(null);
    const highlightHandle = useRef<IHandle | null>(null);
    const highlightAlternateFeatureHandle = useRef<IHandle | null>(null);

    const prevData = useRef({
        currentMapExtent,
        definitionExpression,
        sortValue: sortByValue,
        polygonListCountVal: polygonListCount,
        lineListCountVal: lineListCount,
        pointListCountVal: pointListCount,
    });

    const zoomThreshold = 8;
    const debouncedSetExtent = debounce((view: MapView) => {
        setExtent(view.zoom >= zoomThreshold ? view.extent : null);
    }, 300);

    useEffect(() => {
        updateFilterAndDefinitionExpression();
    }, []);

    useEffect(() => {
        if (showMap && isConfigLoaded && mapRef.current !== null) {
            const loadMap = async () => {
                const view = initializeWebMapView(
                    config.webmapId[config.env],
                    mapRef.current,
                    currentMapExtent
                );

                setView(view);
                const webmap = view.map as WebMap;
                try {
                    await webmap.load();
                    await configureLayers(webmap);
                } catch (e) {
                    console.error('error loading webmap or layers', e);
                }
                setExtent(view.zoom >= zoomThreshold ? view.extent : null);
                captureInitState(webmap);
                addWidgets(view, config);
                setupQueryExtentListener(view);
                MapViewHelper(
                    view,
                    setWfftsAttributes,
                    setLegacyAttributes,
                    navigate
                );
            };
            loadMap()
                .catch((e) => console.error(e))
                .finally(() => setInitLoaded(true));
        }
    }, []);

    useEffect(() => {
        if (view && overrides) {
            const q = new Query();
            // this needs parentheses because sanitizeQueryObject in GeneralService.queryCall adds an extra quote at the end if no parentheses are present
            q.where = `(OverrideType = '${config.ifprsOverrides.overrideTypes.fiscalYear}')`; // make this generic later if needed, no other types currently
            q.outFields = ['*'];
            GeneralService.queryCall(config.ifprsOverrides[config.env].restEndpoint, q).then(
                (results) => {
                    if (results) {
                        const fyConfig = configureFiscalYearOverrides(results, config);
                        dispatch(
                            setFiscalYearOverridesActive({
                                module: 'fuels',
                                isActive: fyConfig.fuels.fiscalYearOverridesActive,
                            })
                        );
                        dispatch(
                            setFiscalYearOverridesDate({
                                module: 'fuels',
                                date: fyConfig.fuels.fiscalYearOverridesDate,
                            })
                        );
                        dispatch(
                            setObjectId({ module: 'fuels', objectId: fyConfig.fuels.objectId })
                        );
                        dispatch(
                            setFiscalYearOverridesActive({
                                module: 'postFire',
                                isActive: fyConfig.postFire.fiscalYearOverridesActive,
                            })
                        );
                        dispatch(
                            setFiscalYearOverridesDate({
                                module: 'postFire',
                                date: fyConfig.postFire.fiscalYearOverridesDate,
                            })
                        );
                        dispatch(
                            setObjectId({
                                module: 'postFire',
                                objectId: fyConfig.postFire.objectId,
                            })
                        );
                    }
                }
            );
        }
    }, [view]);

    useEffect(() => {
        // wait until the map and all dependencies are loaded
        if (view && initLoaded) {
            runMapQuery(
                view,
                definitionExpression,
                isViewingActions,
                isViewingProjects,
                isBasicFilterActive,
                isCustomFilterActive
            );
        }
    }, [view, extent, isViewingProjects, initLoaded]);

    useEffect(() => {
        // wait until the map and all dependencies are loaded
        if (view && definitionExpression && initLoaded) {
            runMapQuery(
                view,
                definitionExpression,
                isViewingActions,
                isViewingProjects,
                isBasicFilterActive,
                isCustomFilterActive
            );
        }
    }, [definitionExpression, view, isViewingProjects, initLoaded]);

    useEffect(() => {
        if (view && extent) {
            const mapViewObj = {
                extent: view.extent,
                scale: view.scale,
                zoom: view.zoom,
                resolution: view.resolution, // Equals the pixels / map unit. About equal to mapView.extent.width/ mapView.width;
            };
            dispatch(setCurrentMapViewObj(mapViewObj));
        }
    }, [view, extent]);

    useEffect(() => {
        //This will set the Projects to visible in the Layer List if they are turned off and the user clicks the Projects radio button
        //Important: This is modifying the layer list outside of the initial creation and without a watchUtil
        if (layerListWidget) {
            layerListWidget?.view?.layerViews?.forEach((ea) => {
                if (ea?.layer && ea?.layer?.title === FieldNames.groupMapLayerTitles.projects) {
                    if (!ea.layer.visible && !isViewingActions) {
                        ea.layer.set('visible', true);
                    }
                }
            });
        }
    }, [isViewingActions, layerListWidget]);

    // handle fetching bookmarks for a user
    useEffect(() => {
        if (view) {
            const fetchBookmarks = async () => {
                try {
                    const allBookmarks = await queryBookmarks(
                        userRoleInfo[0][FieldNames.userBookmarks.usersJoinID]
                    );
                    setExistingBookmarkData(allBookmarks);
                } catch (error) {
                    ErrorService.addErrorLogEntry(error);
                }
                setIsBookmarksLoaded(true);
            };
            fetchBookmarks();
        }
    }, [userRoleInfo, userInfo, view]);

    useEffect(() => {
        if (isBookmarksLoaded && existingBookmarkData && view) {
            // check if widget already exists
            if (!view.ui.find('bookmarkExpandWidget')) {
                const bookmarkWidget = NewBookmarkWidget(view, existingBookmarkData);
                SetBookmarkWidget(
                    view,
                    bookmarkWidget,
                    userRoleInfo,
                    existingBookmarkData,
                    setMapSnackbarOpen,
                    setMapSnackbarMessage,
                    setMapSnackbarSeverity,
                    setIsBookmarksLoaded,
                    userInfo,
                    setExistingBookmarkData
                );

                const bkExpand = new Expand({
                    view: view,
                    content: bookmarkWidget,
                    id: 'bookmarkExpandWidget',
                });
                view.ui.add(bkExpand, { position: 'bottom-left' });
            }
        }
    }, [existingBookmarkData, isBookmarksLoaded, view]);

    useEffect(() => {
        if (view && mapGeoExtentToLoad) {
            emitCustomEvent(Events.ZoomTo, mapGeoExtentToLoad);
            setMapGeoExtentToLoad(null);
            const mapViewObj = {
                extent: view.extent,
                scale: view.scale,
                zoom: view.zoom,
                resolution: view.resolution, // Equals the pixels / map unit. About equal to mapView.extent.width/ mapView.width;
            };
            dispatch(setCurrentMapViewObj(mapViewObj));
        }
    }, [view, mapGeoExtentToLoad]);

    useEffect(() => {
        if (!spatialFilterGraphicsLayer) {
            return;
        }

        spatialFilterGraphicsLayer.removeAll();
        const latLonUniverse = new Polygon({
            spatialReference: new SpatialReference({ wkid: 4269 }),
        });
        latLonUniverse.rings = [
            [
                [-179, -80],
                [-179, 80],
                [179, 80],
                [179, -80],
                [-179, -80],
            ],
        ];

        const universeWebMercator = WebMercatorUtils.geographicToWebMercator(latLonUniverse);

        if (filterGeometry) {
            let geometry = geometryJsonUtils.fromJSON(filterGeometry);

            if (geometry) {
                if (geometry.spatialReference.isGeographic) {
                    geometry = WebMercatorUtils.geographicToWebMercator(geometry);
                }

                const maskGeometry = GeometryEngine.difference(universeWebMercator, geometry);

                spatialFilterGraphicsLayer.add({
                    geometry: maskGeometry,
                    symbol: {
                        type: 'simple-fill',
                        style: 'solid',
                        color: 'rgba(66, 66, 66, 0.5)',
                        outline: {
                            color: 'black',
                            width: 2,
                        },
                    },
                });
            }
        }
    }, [filterGeometry, spatialFilterGraphicsLayer]);

    useEffect(() => {
        return () => {
            prevData.current.definitionExpression = definitionExpression;
            prevData.current.sortValue = sortByValue;
            prevData.current.polygonListCountVal = polygonListCount;
            prevData.current.lineListCountVal = lineListCount;
            prevData.current.pointListCountVal = pointListCount;
        };
    }, [
        definitionExpression,
        polygonListCount,
        lineListCount,
        pointListCount,
        sortByValue,
        prevData,
    ]);

    //Load Listeners
    SetListeners(
        setLegacyAttributes,
        setPlannedFeatureAttributes,
        setActualIsNew,
        setActualIsTreatment,
        setWfftsAttributes,
        setExistingGeoBeforeEdit
    );
    IconListeners(
        wfigsLayer?.current,
        wfftsLayer?.current,
        ifphLayer?.current,
        legacyTreatmentsLayer,
        fuelsActualActivities?.current,
        fuelsActualsTreatments?.current,
        fuelsEstimatedActivities?.current,
        fuelsEstimatedTreatments?.current,
        postFireActualsActivities?.current?.polygonLayer,
        postFireActualsTreatments?.current?.polygonLayer,
        postFireEstimatedActivities?.current?.polygonLayer,
        postFireEstimatedTreatments?.current?.polygonLayer
    );
    GetListeners(
        plannedFeatureAttributes,
        actualIsNew,
        actualIsTreatment,
        currentMapExtent,
        modifiedGraphicsLayer,
        wfftsAttributes,
        legacyAttributes
    );
    ResetListeners(view);
    RefreshListeners(
        modifiedGraphicsLayer,
        view,
        existingGeoBeforeEdit,
        fuelsEstimatedTreatments?.current,
        fuelsActualsTreatments?.current,
        fuelsEstimatedActivities?.current,
        fuelsActualActivities?.current,
        postFireProjectsPolygonLayer?.current,
        postFireEstimatedTreatments?.current,
        postFireActualsTreatments?.current,
        postFireEstimatedActivities?.current,
        postFireActualsActivities?.current,
        postFireValuesPolygonLayer?.current,
        highlightHandle,
        highlightAlternateFeatureHandle
    );
    CopyListeners(
        isFilterOnly,
        view,
        getLegacyModifiedAttributes,
        userRoleInfo,
        modifiedGraphicsLayer,
        userInfo,
        getWFFTSModifiedAttributes,
        navigate
    );
    CreateListeners(
        isFilterOnly,
        view,
        userRoleInfo,
        userInfo,
        getWFFTSModifiedAttributes,
        navigate,
        modifiedGraphicsLayer,
        fuelsEstimatedTreatments?.current,
        fuelsEstimatedActivities?.current,
    );

    ModifiedListeners(view, modifiedGraphicsLayer);
    MapListeners(view, setMapGeoExtentToLoad);
    WFIGSValueSelectionListener(view);

    function runMapQuery(
        view: MapView,
        definitionExpression: string,
        isViewingActions: boolean,
        isViewingProjects: boolean,
        isBasicFilterActive: boolean,
        isCustomFilterActive: boolean
    ) {
        if (isViewingActions) {
            if (isBasicFilterActive || isCustomFilterActive) {
                setLoadingStatesAndQueryCounts(view, definitionExpression);
            }
            setLoadingStatesAndQueryTreatments(view, definitionExpression);
        } else if (isViewingProjects) {
            setProjectLoadingStatesAndQueryCounts();
        }
    }

    function captureInitState(map: __esri.Map) {
        map.allLayers.forEach((l: any) => {
            l._initState = {
                definitionExpression: l?.definitionExpression,
                visible: l.visible,
            };
        });
    }

    function addWidgets(mapView: __esri.MapView, config: any) {
        // todo: update layerlist with custom listItemCreated functionality
        const toggle = new BasemapToggle({ view: mapView, nextBasemap: 'hybrid' });

        const layerList = new LayerList({
            view: mapView,
            listItemCreatedFunction: (event) => {
                const item = event.item;
                if (
                    item &&
                    item.layer &&
                    item.layer.title === FieldNames.mapLayers.jurisdictionalUnits
                ) {
                    item.actionsSections = [
                        [
                            {
                                title: 'Labels',
                                className: 'esri-icon-labels',
                                id: 'labels',
                            },
                        ],
                    ];
                }
                if (item?.layer?.type != 'group') {
                    // don't show legend twice
                    item.panel = {
                        content: 'legend',
                    };
                }
                if (
                    config.hiddenLayersGroupTitle[config.env]
                        .map((i: string) => i.toLowerCase())
                        .includes(item.title.toLowerCase()) &&
                    item.layer
                ) {
                    item.layer.listMode = 'hide';
                }
            },
        });
        const layerListExpand = new Expand({
            view: mapView, // Your MapView or SceneView instance
            content: layerList, // Set the LayerList instance as the content of the Expand widget
        });
        setLayerListWidget(layerList);

        mapView.ui.add(layerListExpand, 'top-left');
        mapView.ui.add(toggle, 'bottom-right');

        const searchWidget = GetSearchWidget(mapView);
        setMapSearchWidget(searchWidget);
        mapView.ui.add(searchWidget, { position: 'top-right', index: 0 });

        const measureButtons = getMeasureButtons(mapView);
        setMapMeasureWidget(measureButtons);
        mapView.ui.add(measureButtons, { position: 'top-right', index: 1 });
    }

    async function queryBookmarks(userId: string) {
        try {
            const resp: __esri.Graphic[] = await UserBookmarksService.getBookmarksByUser(userId);
            if (resp && resp.length > 0) {
                let allBookmarks: Bookmark[] = [];
                resp.forEach((item: Graphic) => {
                    if (item?.attributes) {
                        const existingBookmark = new Bookmark({
                            [FieldNames.userBookmarks.objectID]:
                                item.attributes[FieldNames.userBookmarks.objectID],
                            name: item.attributes[FieldNames.userBookmarks.name],
                            thumbnail: {
                                url: item.attributes[FieldNames.userBookmarks.thumbnail],
                            },
                            viewpoint: {
                                rotation: item.attributes[FieldNames.userBookmarks.rotation],
                                scale: item.attributes[FieldNames.userBookmarks.scale],
                                targetGeometry: new Point({
                                    latitude: item.attributes[FieldNames.userBookmarks.latitude],
                                    longitude: item.attributes[FieldNames.userBookmarks.longitude],
                                }),
                            },
                        });
                        allBookmarks.push(existingBookmark);
                    }
                });
                return allBookmarks;
            } else {
                return [];
            }
        } catch (e) {
            throw e;
        }
    }

    async function queryMap(extent: __esri.Extent, defExpr: string = '') {
        const controller = handleFeatureAbortController();

        // ensure all layers are present
        if (checkCoreLayersMissing()) {
            if (view && view.map) {
                // try to reconfigure layers if missing
                await configureLayers(view?.map as WebMap);
                if (checkCoreLayersMissing()) {
                    return Promise.resolve(null);
                }
            } else {
                return Promise.resolve(null);
            }
        }

        const outFieldsArray = [
            FieldNames.featureFields.objectID,
            FieldNames.featureFields.id,
            FieldNames.featureFields.name,
            FieldNames.featureFields.class,
            FieldNames.featureFields.calculatedArea,
            FieldNames.featureFields.areaUnit,
            FieldNames.featureFields.type,
            FieldNames.featureFields.category,
            FieldNames.featureFields.status,
            FieldNames.featureFields.isBIL,
            FieldNames.featureFields.fundingUnit,
            FieldNames.featureFields.state,
            FieldNames.featureFields.initiationFiscalYear,
            FieldNames.featureFields.completionFiscalYear,
            FieldNames.featureFields.hasGroup,
            FieldNames.featureFields.hasActual,
            FieldNames.featureFields.groupCount,
            FieldNames.featureFields.estimatedTotalCost,
            FieldNames.featureFields.isCarryover,
            FieldNames.featureFields.isContract,
            FieldNames.featureFields.isAgreement,
            FieldNames.featureFields.departmentApprovalDate,
            FieldNames.featureFields.agencyApprovalDate,
            FieldNames.featureFields.regionalApprovalDate,
            FieldNames.featureFields.localApprovalDate,
            FieldNames.featureFields.isFunded,
            FieldNames.featureFields.entityType,
            FieldNames.featureFields.joinFeature,
            FieldNames.featureFields.joinID,
            FieldNames.featureFields.geometryObjectID,
            FieldNames.featureFields.isProgressiveReporting,
            FieldNames.featureFields.localID,
        ];

        const promises = [
            ...queryIfApplicable(
                postFireEstimatedTreatments.current,
                fuelsEstimatedTreatments?.current,
                filterFeatureSelection,
                'Estimated Treatment',
                dataset,
                defExpr,
                outFieldsArray,
                sortByValue,
                controller
            ),
            ...queryIfApplicable(
                postFireEstimatedActivities.current,
                fuelsEstimatedActivities?.current,
                filterFeatureSelection,
                'Estimated Activity',
                dataset,
                defExpr,
                outFieldsArray,
                sortByValue,
                controller
            ),
            ...queryIfApplicable(
                postFireActualsTreatments.current,
                fuelsActualsTreatments?.current,
                filterFeatureSelection,
                'Actual Treatment',
                dataset,
                defExpr,
                outFieldsArray,
                sortByValue,
                controller
            ),
            ...queryIfApplicable(
                postFireActualsActivities.current,
                fuelsActualActivities?.current,
                filterFeatureSelection,
                'Actual Activity',
                dataset,
                defExpr,
                outFieldsArray,
                sortByValue,
                controller
            ),
        ];
        // features
        return Promise.all(promises);
    }

    function queryIfApplicable(
        layerService: LayerGroupService,
        featureLayer: FL,
        filterFeatureSelection: string[],
        selectionName: string,
        dataset: string,
        defExpr: string,
        outFieldsArray: string[],
        sortByValue: string,
        controller: AbortController
    ) {
        const defaultFeatureSet = new FeatureSet();
        if (!filterFeatureSelection.includes(selectionName) && filterFeatureSelection.length > 0) {
            return [Promise.resolve(defaultFeatureSet), Promise.resolve(defaultFeatureSet)];
        }
        const includePostFire =
            dataset === FieldNames.datasets.postfire || dataset === FieldNames.datasets.both;
        const includeFuels =
            dataset === FieldNames.datasets.fuels || dataset === FieldNames.datasets.both;
        return [
            includePostFire
                ? layerService.queryLayers(
                      defExpr,
                      outFieldsArray,
                      filterGeometry ? filterGeometry : extent,
                      pointListCount,
                      lineListCount,
                      polygonListCount,
                      0,
                      sortByValue,
                      controller
                  )
                : Promise.resolve(defaultFeatureSet),
            includeFuels
                ? queryAllFeatures(
                      buildQuery(
                          featureLayer as FL,
                          defExpr,
                          outFieldsArray,
                          filterGeometry ? filterGeometry : extent,
                          polygonListCount,
                          0,
                          sortByValue
                      ),
                      featureLayer as FL,
                      sortByValue,
                      controller
                  )
                : Promise.resolve(defaultFeatureSet),
        ];
    }

    async function queryMapFeatureCount(defExpr: string = '') {
        const controller = new AbortController();
        if (!featureCountAbortController || featureCountAbortController.signal.aborted) {
            setFeatureCountAbortController(controller);
        } else if (featureCountAbortController) {
            featureCountAbortController.abort();
        }

        // ensure all layers are present
        if (checkCoreLayersMissing()) {
            if (view && view.map) {
                // try to reconfigure layers if missing
                await configureLayers(view?.map as WebMap);
                if (checkCoreLayersMissing()) {
                    return Promise.resolve(null);
                }
            } else {
                return Promise.resolve(null);
            }
        }

        const promises = [
            ...queryCountIfApplicable(
                postFireEstimatedTreatments.current,
                fuelsEstimatedTreatments.current,
                filterFeatureSelection,
                'Estimated Treatment',
                dataset,
                defExpr,
                controller
            ),
            ...queryCountIfApplicable(
                postFireEstimatedActivities.current,
                fuelsEstimatedActivities.current,
                filterFeatureSelection,
                'Estimated Activity',
                dataset,
                defExpr,
                controller
            ),
            ...queryCountIfApplicable(
                postFireActualsTreatments.current,
                fuelsActualsTreatments.current,
                filterFeatureSelection,
                'Actual Treatment',
                dataset,
                defExpr,
                controller
            ),
            ...queryCountIfApplicable(
                postFireActualsActivities.current,
                fuelsActualActivities.current,
                filterFeatureSelection,
                'Actual Activity',
                dataset,
                defExpr,
                controller
            ),
        ];

        // features
        return Promise.all(promises);
    }

    function checkCoreLayersMissing() {
        return (
            !postFireEstimatedTreatments.current ||
            !postFireEstimatedActivities.current ||
            !postFireActualsTreatments.current ||
            !postFireActualsActivities.current ||
            !fuelsEstimatedTreatments?.current ||
            !fuelsEstimatedActivities?.current ||
            !fuelsActualsTreatments?.current ||
            !fuelsActualActivities?.current
        );
    }

    function queryCountIfApplicable(
        layerService: LayerGroupService,
        featureLayer: FL,
        filterFeatureSelection: string[],
        selectionName: string,
        dataset: string,
        defExpr: string,
        controller: AbortController
    ) {
        if (!filterFeatureSelection.includes(selectionName) && filterFeatureSelection.length > 0) {
            return [Promise.resolve(0), Promise.resolve(0)];
        }
        const includePostFire =
            dataset === FieldNames.datasets.postfire || dataset === FieldNames.datasets.both;
        const includeFuels =
            dataset === FieldNames.datasets.fuels || dataset === FieldNames.datasets.both;

        return [
            includePostFire
                ? layerService?.queryFeatureCount(
                      defExpr,
                      filterGeometry ? filterGeometry : extent,
                      controller
                  )
                : Promise.resolve(0),
            includeFuels
                ? queryFeatureCount(
                      buildCountQuery(
                          featureLayer as FL,
                          defExpr,
                          filterGeometry ? filterGeometry : extent
                      ),
                      featureLayer as FL,
                      controller
                  )
                : Promise.resolve(0),
        ];
    }

    function handleQueryResults(results: ([] | FeatureSet | FeatureSet[])[]) {
        const normalized: FeatureSet[][] = results.map((r) => (!Array.isArray(r) ? [r] : r));
        // flatten output results to attributes only
        dispatch(setTreatments(flattenToAttributes([...normalized[0], ...normalized[1]])));
        dispatch(setActivities(flattenToAttributes([...normalized[2], ...normalized[3]])));
        dispatch(setActualsTreatments(flattenToAttributes([...normalized[4], ...normalized[5]])));
        dispatch(setActualsActivities(flattenToAttributes([...normalized[6], ...normalized[7]])));

        //@ts-ignore
        dispatch(setIsTreatmentCountQuerying(false));
        // @ts-ignore
        dispatch(setIsActualTreatmentCountQuerying(false));
        // @ts-ignore
        dispatch(setIsActivityCountQuerying(false));
        // @ts-ignore
        dispatch(setIsActualActivityCountQuerying(false));
        // @ts-ignore
        dispatch(setShowListCardLoader(false));
    }

    function handleFeatureAbortController() {
        let controller = new AbortController();
        if (!featureAbortController || featureAbortController.signal.aborted) {
            setFeatureAbortController(controller);
        } else {
            featureAbortController.abort();
            setFeatureAbortController(controller);
        }
        return controller;
    }

    function handleCountQueryResults(results: (number | number[])[]) {
        const normalized: number[][] = results.map((r) => (!Array.isArray(r) ? [r] : r));
        const estimatedTreatmentCount = totalReducer([...normalized[0], ...normalized[1]]);
        const estimatedActivityCount = totalReducer([...normalized[2], ...normalized[3]]);
        const actualTreatmentCount = totalReducer([...normalized[4], ...normalized[5]]);
        const actualActivityCount = totalReducer([...normalized[6], ...normalized[7]]);
        dispatch(setTreatmentCount(estimatedTreatmentCount));
        dispatch(setActivityCount(estimatedActivityCount));
        dispatch(setActualTreatmentCount(actualTreatmentCount));
        dispatch(setActualActivityCount(actualActivityCount));
        dispatch(setTreatmentCountQueryError(false));
        dispatch(setActivityCountQueryError(false));
        dispatch(setActualTreatmentCountQueryError(false));
        dispatch(setActualActivityCountQueryError(false));

        dispatch(setIsTreatmentCountQuerying(false));
        dispatch(setIsActivityCountQuerying(false));
        dispatch(setIsActualTreatmentCountQuerying(false));
        dispatch(setIsActualActivityCountQuerying(false));
    }

    function totalReducer(nums: number[]) {
        return nums.reduce((acc, curr) => acc + curr, 0);
    }

    async function configureLayers(map: __esri.WebMap) {
        // postfire
        await setupPostFireLayers(map);
        fuelsEstimatedTreatments.current = await getLayerFromMapByPortalIdAndSetInitState(
            map,
            config.fuels[config.env].polygons.estimatedTreatments.id
        );
        configureActions(
            fuelsEstimatedTreatments.current,
            estimatedTreatmentActions.concat(additionalPolygonActions)
        );
        fuelsActualsTreatments.current = await getLayerFromMapByPortalIdAndSetInitState(
            map,
            config.fuels[config.env].polygons.actualTreatments.id
        );
        configureActions(
            fuelsActualsTreatments.current,
            actualTreatmentActions.concat(additionalPolygonActions)
        );
        fuelsEstimatedActivities.current = await getLayerFromMapByPortalIdAndSetInitState(
            map,
            config.fuels[config.env].polygons.estimatedActivities.id
        );
        configureActions(
            fuelsEstimatedActivities.current,
            estimatedActivityActions.concat(additionalPolygonActions)
        );
        fuelsActualActivities.current = await getLayerFromMapByPortalIdAndSetInitState(
            map,
            config.fuels[config.env].polygons.actualActivities.id
        );
        configureActions(
            fuelsActualActivities.current,
            actualActivityActions.concat(additionalPolygonActions)
        );
        postFireProjectsPolygonLayer.current = await getLayerFromMapByPortalIdAndSetInitState(
            map,
            config.ifprsProjects[config.env].portalItem.id
        );

        postFireValuesPolygonLayer.current = await getLayerFromMapByPortalIdAndSetInitState(
            map,
            config.postfire[config.env].valuesPolygons.id
        );
        configureActions(postFireProjectsPolygonLayer.current, projectActions);

        // graphics layers
        // spatial filtering
        const filterGraphicsLayer = new GraphicsLayer({ listMode: 'hide' });
        map.add(filterGraphicsLayer);
        setSpatialFilterGraphicsLayer(filterGraphicsLayer);
        // geometry modifications
        const tempModifiedGraphicsLayer = new GraphicsLayer();
        tempModifiedGraphicsLayer.listMode = 'hide';
        setModifiedGraphicsLayer(tempModifiedGraphicsLayer);
        map.add(tempModifiedGraphicsLayer);

        // additional data layers
        legacyTreatmentsLayer.current = await getLayerFromMapByUrl(
            map,
            config.completedTreatments.url
        );
        configureActions(legacyTreatmentsLayer?.current, config.completedTreatments.actions);
        wfftsLayer.current = await getLayerFromMapByUrl(map, config.WFFTS[config.env].url);
        configureActions(wfftsLayer?.current, config.WFFTS.actions);
        ifphLayer.current = await getLayerFromMapByUrl(map, config.IFPH[config.env].url);
        configureActions(ifphLayer?.current, config.IFPH.actions);
        wfigsLayer.current = await getLayerFromMapByUrl(map, config.WFIGS[config.env].url);
        configureActions(wfigsLayer?.current, config.WFIGS.actions);

        // If url structure indicates initial page load is an existing treatment or activity, highlight the feature.
        // Has to be separate from details/index highlight event emission
        highlightFeatureFromURL(location);
    }

    async function setupPostFireLayers(map: __esri.WebMap) {
        postFireEstimatedTreatments.current = new LayerGroupService(map, 'treatments');

        await postFireEstimatedTreatments.current?.configureLayers(
            config.postfire[config.env].points.estimatedTreatments.id,
            config.postfire[config.env].lines.estimatedTreatments.id,
            config.postfire[config.env].polygons.estimatedTreatments.id
        );

        postFireEstimatedTreatments.current?.configureLayerActions(
            estimatedTreatmentActions,
            estimatedTreatmentActions,
            estimatedTreatmentActions.concat(additionalPolygonActions)
        );

        postFireActualsTreatments.current = new LayerGroupService(map, 'treatments');

        await postFireActualsTreatments.current?.configureLayers(
            config.postfire[config.env].points.actualTreatments.id,
            config.postfire[config.env].lines.actualTreatments.id,
            config.postfire[config.env].polygons.actualTreatments.id
        );

        postFireActualsTreatments.current?.configureLayerActions(
            actualTreatmentActions,
            actualTreatmentActions,
            actualTreatmentActions.concat(additionalPolygonActions)
        );

        postFireEstimatedActivities.current = new LayerGroupService(map, 'activities');

        await postFireEstimatedActivities.current?.configureLayers(
            config.postfire[config.env].points.estimatedActivities.id,
            null,
            config.postfire[config.env].polygons.estimatedActivities.id
        );

        postFireEstimatedActivities?.current?.configureLayerActions(
            estimatedActivityActions,
            estimatedActivityActions,
            estimatedActivityActions.concat(additionalPolygonActions)
        );

        postFireActualsActivities.current = new LayerGroupService(map, 'activities');

        await postFireActualsActivities.current?.configureLayers(
            config.postfire[config.env].points.actualActivities.id,
            null,
            config.postfire[config.env].polygons.actualActivities.id
        );

        postFireActualsActivities.current?.configureLayerActions(
            actualActivityActions,
            actualActivityActions,
            actualActivityActions.concat(additionalPolygonActions)
        );
    }

    function setupQueryExtentListener(view: __esri.MapView) {
        if (viewExtentListener) {
            viewExtentListener.remove();
        }
        viewExtentListener = reactiveUtils.when(
            () => view.stationary,
            () => {
                debouncedSetExtent(view);
            }
        );
    }

    function debounce<T extends (...args: any[]) => void>(func: T, wait: number) {
        let timeout: ReturnType<typeof setTimeout> | null;
        return function (this: any, ...args: Parameters<T>) {
            if (timeout) clearTimeout(timeout);
            timeout = setTimeout(() => func.apply(this, args), wait);
        };
    }

    function setLoadingStatesAndQueryTreatments(view: MapView, defExpr?: string | null) {
        dispatch(setIsTreatmentCountQuerying(true));
        dispatch(setIsActualTreatmentCountQuerying(true));
        dispatch(setIsActivityCountQuerying(true));
        dispatch(setIsActualActivityCountQuerying(true));
        dispatch(setShowListCardLoader(true));
        queryMap(view.extent, defExpr)
            .then((r) => {
                if (r) {
                    handleQueryResults(r);
                }
            })
            .catch((e) => {
                handleError(e);
            });
    }

    function handleError(e: Error) {
        if (e.name !== 'AbortError') {
            console.error(e);
            dispatch(setTreatmentCount(0));
            dispatch(
                setAlertMessageObj({
                    ...alertMessageObj,
                    severity: 'error',
                    child: (
                        <Typography sx={{ marginLeft: '5px', marginRight: '5px' }}>
                            There is an error with your map, please use the <b>Reset Map View</b>{' '}
                            option in the upper right menu.
                        </Typography>
                    ),
                    callback: null,
                })
            );
        } else {
            console.warn('Request aborted');
        }
    }

    function setLoadingStatesAndQueryCounts(view: MapView, defExpr?: string | null) {
        dispatch(setIsTreatmentCountQuerying(true));
        dispatch(setIsActualTreatmentCountQuerying(true));
        dispatch(setIsActivityCountQuerying(true));
        dispatch(setIsActualActivityCountQuerying(true));
        queryMapFeatureCount(defExpr)
            .then((r) => {
                if (r) {
                    handleCountQueryResults(r);
                }
            })
            .catch((e) => {
                handleError(e);
            });
    }

    function setProjectLoadingStatesAndQueryCounts() {
        queryProjects()
            .then((r) => {
                if (r) {
                    dispatch(setVisibleProjects(r.features.map((f) => f.attributes)));
                }
            })
            .catch((e) => {
                handleError(e);
            });
    }

    async function queryProjects() {
        if (!isViewingProjects || !postFireProjectsPolygonLayer.current) {
            return Promise.resolve(null);
        }

        const controller = new AbortController();
        if (!projectAbortController || projectAbortController.signal.aborted) {
            setFeatureCountAbortController(controller);
        } else if (projectAbortController) {
            projectAbortController.abort();
        }

        const outFields = [
            FieldNames.projectFields.name,
            FieldNames.projectFields.fireName,
            FieldNames.projectFields.wildfireAcres,
            FieldNames.projectFields.fireCode,
            FieldNames.projectFields.irwinID,
            FieldNames.projectFields.fireDiscoveryDate,
            FieldNames.projectFields.containmentDate,
            FieldNames.projectFields.ignitionFiscalYear,
            FieldNames.projectFields.id,
            FieldNames.projectFields.objectID,
            FieldNames.projectFields.notes,
            FieldNames.projectFields.entityType,
            FieldNames.projectFields.isFirePerimeter,
        ];

        return queryAllFeatures(
            buildQuery(
                postFireProjectsPolygonLayer.current,
                '(OBJECTID IS NOT NULL)',
                outFields,
                extent,
                2000,
                0,
                ''
            ),
            postFireProjectsPolygonLayer.current,
            projectSortByValue,
            controller
        );
    }

    return (
        <Box id='viewContainer' style={{ maxWidth: '100%' }} ref={mapRef}>
            {view ? (
                <DrawToolbar view={view} modifiedGraphicsLayer={modifiedGraphicsLayer} />
            ) : null}
        </Box>
    );
}
